import { type PropsWithChildren, useEffect, useRef } from "react";

import parseEncodedJSON from "@/lib/parseEncodedJSON";
import { CitywireImagesSchema, ConfigPartSchema } from "@/types";
import { SRP } from "hsl-core/providers";
import Context from "./context";
import createStore, { type FundExplorerStore } from "./storeCreator";
import type { StoreState } from "./types";

type Props = {
	initialState?: Partial<StoreState>;
	tenant?: string;
};

export default function StoreProvider({
	children,
	initialState,
}: PropsWithChildren<Props>) {
	const store = useRef<FundExplorerStore>(null);
	const cityWireConfigId = initialState?.config?.cityWireConfigId;
	const { data: cityWireConfig } = SRP.usePart(
		!!cityWireConfigId,
		{
			id: cityWireConfigId,
		},
		ConfigPartSchema,
	);
	const cityWireImages = cityWireConfig?.config
		? CitywireImagesSchema.safeParse(parseEncodedJSON(cityWireConfig.config))
		: null;

	useEffect(() => {
		if (!cityWireImages) return;
		if (!store.current) return;
		const currentState = store.current.getState();
		store.current.setState(
			{
				...currentState,
				cityWireImages: cityWireImages?.success
					? cityWireImages.data.citywireRating
					: {},
			},
			false,
		);
	}, [cityWireImages]);

	if (!store.current) {
		store.current = createStore(initialState);
	}

	return <Context value={store.current}>{children}</Context>;
}
